import React, { useContext, useEffect, useState } from "react"

import { Form, Formik } from "formik"
import { gql } from "urql"
import * as Yup from "yup"

import { AlertLevel } from "~tailwindui/types/enums"

import { AlertMessageBox } from "~tailwindui/Basics"

import EventEmitter from "src/EventEmitter"
import airbrake from "src/airbrake-notifier"
import { studentAssessmentOptionsWithIcons } from "src/enums"
import { studentClient as client } from "src/urql-client"

import { useMutation } from "hooks/urql"

import {
  FormFailures,
  RadioField,
  SubmitButton,
  handleFailure,
} from "components/Forms/Formik/hookComponents"
import ModalWithProvidedBody from "components/ModalWithProvidedBody"

import CloudroomContext from "../CloudroomContext"

const ValidationSchema = Yup.object().shape({
  studentAssessment: Yup.string().required("Please select an option"),
})

const StudentAssessmentModal = () => {
  const { session, localUser } = useContext(CloudroomContext)
  const [assessmentOpen, setAssessmentOpen] = useState(false)

  useEffect(() => {
    if (localUser.type !== "Student") return

    const onStudentAssessment = () => setAssessmentOpen(true)
    EventEmitter.subscribe("student_assessment", onStudentAssessment)

    return () =>
      EventEmitter.unsubscribe("student_assessment", onStudentAssessment)
  }, [localUser.type])

  const formState = { sessionId: session.id, studentAssessment: "" }
  const [complete, setComplete] = useState(false)
  const [, submitAssessment]: any[] = useMutation(
    studentAssessmentMutation,
    client
  )

  const handleSubmit = (values, actions) => {
    submitAssessment(values)
      .then(
        result => {
          if (result.error) {
            airbrake.notify({
              error: result.error,
              name: "StudentAssessmentModal#handleSubmit",
              params: {
                message: result.error?.message,
              },
            })
            handleFailure(actions)
            return
          }

          const { success, errorMessages } = result.data.submitStudentAssessment
          if (errorMessages) {
            handleFailure(
              actions,
              errorMessages.map(message => ({ message }))
            )
            return
          }
          if (success) {
            actions.setStatus(null)
            setComplete(true)
            setTimeout(() => setAssessmentOpen(false), 3000)
          }
        },
        () => handleFailure(actions)
      )
      .catch(() => handleFailure(actions))
  }

  if (!assessmentOpen) return null

  return (
    <ModalWithProvidedBody
      isOpen
      hideTrigger
      modalClassName="bootstrap-modal"
      modalTitle="How is your session going?"
      afterOpenModal={() => setComplete(false)}
      hideCloseButton
    >
      <Formik
        initialValues={formState}
        onSubmit={handleSubmit}
        validationSchema={ValidationSchema}
      >
        <Form>
          <div className="modal-body">
            <RadioField
              name="studentAssessment"
              options={studentAssessmentOptionsWithIcons}
            />
          </div>
          <div className="modal-footer flex-col">
            <FormFailures />
            {complete && (
              <AlertMessageBox level={AlertLevel.Success}>
                Thanks so much!
              </AlertMessageBox>
            )}
            {!complete ? <SubmitButton text="Submit Feedback" /> : null}
          </div>
        </Form>
      </Formik>
    </ModalWithProvidedBody>
  )
}

const studentAssessmentMutation = gql`
  mutation ($sessionId: ID!, $studentAssessment: StudentAssessmentEnum!) {
    submitStudentAssessment(
      sessionId: $sessionId
      studentAssessment: $studentAssessment
    ) {
      success
      errorMessages
    }
  }
`

export default StudentAssessmentModal
